(ns machtfit.product-form.who
  (:require [ajax.core :refer [POST]]
            [cljs-time.core :refer [before? plus today weeks]]
            [clojure.set :refer [rename-keys]]
            [machtfit.dashboard-widgets :as widgets]
            [machtfit.datetime :as datetime]
            [machtfit.human :as human]
            [machtfit.product-form.core :as product-form]
            [machtfit.product-form.fileupload :refer [fileupload]]
            [machtfit.product-form.inhibit :as inhibit]
            [machtfit.product-form.review :as review]
            [machtfit.product-form.section :refer [show-next-section]]
            [machtfit.utils :refer [string-or-default]]
            [re-frame.core :as rf]
            [reagent.ratom :refer [reaction]]))

(rf/reg-sub ::current-certificate
  :<- [:get [:certificates]]
  :<- [:get [:product :who :certificate-id]]

  (fn [[certificates current-certificate-id] _]
    ((or certificates {}) current-certificate-id)))

(defn- expired? [certificate]
  (let [valid-until (datetime/string->date (certificate :valid-until-date))]
    (and (some? valid-until) (before? valid-until (today)))))

(defn- expires-soon? [certificate]
  (let [valid-until (datetime/string->date (certificate :valid-until-date))]
    (and (some? valid-until) (before? valid-until (plus (today) (weeks 2))))))

(defn- current-and-in-use? [certificate]
  (and (not (expired? certificate))
       (:in-use? certificate)))

(rf/reg-sub ::visible-certificates
  :<- [:get [:certificates]]
  :<- [:get [:ui :show-all-certificates?]]
  :<- [::current-certificate]

  (fn [[certificates show-all? current-certificate] _]
    (let [all-certificates (vals certificates)
          likely-certificates (filter current-and-in-use? all-certificates)
          certificate-selected? (not (nil? current-certificate))
          visible-certificates (if (or show-all?
                                       (and certificate-selected?
                                            (not (some #(= % current-certificate) likely-certificates))))
                                 all-certificates
                                 likely-certificates)]
      (sort-by (juxt (complement current-and-in-use?) :name)
               visible-certificates))))

(defn- set-certificate-id [current-db new-certificate-id]
  (-> current-db
      (assoc-in [:product :who :certificate-id] new-certificate-id)
      (assoc-in [:ui :last-focused-config-section] :who)))

(rf/reg-event-db ::set-certificate-id
  (fn [db [_ new-certificate-id]]
    (set-certificate-id db new-certificate-id)))

(rf/reg-sub ::certificates-hidden?
  :<- [:get [:certificates]]
  :<- [::visible-certificates]

  (fn [[certificates visible-certificates] _]
    (> (count certificates) (count visible-certificates))))

(rf/reg-sub ::has-certificates?
  :<- [:get [:certificates]]

  (fn [certificates _]
    (not (empty? certificates))))

(defn- certificate-label [certificate]
  (let [valid-until (-> certificate
                        :valid-until-date
                        datetime/string->date
                        human/dateformat-long)
        valid-str (cond
                    (not valid-until) "unbegrenzt gültig"
                    (expired? certificate) [:span {:style {:color "red"}} valid-until]
                    :else valid-until)]
    [:span
     (string-or-default (:name certificate) "(Ohne Namen)")
     ", "
     valid-str]))

(defn- all-certificates []
  (let [disabled? @(rf/subscribe [::is-editing-certificate?])
        visible-certificates @(rf/subscribe [::visible-certificates])
        current-certificate @(rf/subscribe [::current-certificate])
        has-certificates? @(rf/subscribe [::has-certificates?])
        certificates-hidden? @(rf/subscribe [::certificates-hidden?])]
    [:div
     [widgets/radio-options
      {:name "certificate-id"
       :current-value (:id current-certificate)
       :choices (mapv (juxt :id certificate-label) visible-certificates)
       :on-select #(rf/dispatch [::set-certificate-id %])
       :disabled? disabled?}]
     (when certificates-hidden?
       [:a.btn-link {:on-click #(rf/dispatch [:toggle [:ui :show-all-certificates?]])
                     :style {:cursor "pointer" :display "inline-block" :margin "10px auto"}}
        "abgelaufene und unbenutzte Zertifikate anzeigen"])
     (when has-certificates?
       [widgets/radio-option
        {:name "certificate-id"
         :label-text "kein Zertifikat"
         :on-select #(rf/dispatch [::set-certificate-id nil])
         :checked? (nil? current-certificate)
         :disabled? disabled?}])]))

(defn certificate-expires-hint [style]
  (let [certificate @(rf/subscribe [::current-certificate])
        expired?' (some-> certificate expired?)
        expires-soon?' (some-> certificate expires-soon?)
        valid-until (some-> certificate :valid-until-date datetime/string->date)
        describe-date (some-> valid-until human/describe-date)
        valid-until-suffix (when describe-date (str " (" describe-date ")"))
        valid-until-str (str (human/dateformat-long valid-until) valid-until-suffix)]
    (when expires-soon?'
      [:span {:style (merge {:color "red"} style)}
       (if expired?'
         "Zertifikat ist nicht mehr gültig"
         (str "Zertifikat läuft bald ab" valid-until-suffix))
       [widgets/tooltip {:icon "icon-question-sign"
                         :content [:span "Das Zertifikat für dieses Angebot "
                                   (if expired?'
                                     "lief"
                                     "läuft")
                                   " am " valid-until-str " ab. "
                                   (if expired?'
                                     "Damit"
                                     "Nach diesem Datum")
                                   " ist keine Bezuschussung "
                                   "durch den Arbeitgeber nach § 3 Nr. 34 EStG mehr möglich. "
                                   "Das Angebot wird dadurch für den Mitarbeiter in der Regel teurer. "
                                   [:strong "Hinterlegen Sie ein aktuelles Zertifikat"] ", damit Mitarbeiter weiterhin "
                                   "von der Bezuschussung durch ihren Arbeitgeber profitieren können."]}]])))

(defn- preview-certificate []
  (let [current-certificate @(rf/subscribe [::current-certificate])
        certificate-url (:url current-certificate)
        valid-until (:valid-until-date current-certificate)]
    [:div.card
     [:div.card-content
      [:div.card-title (string-or-default (:name current-certificate) "(Ohne Namen)")]
      (when certificate-url
        [:div.right [:a.btn {:href certificate-url :target "_blank"}
                     [:i.material-icons.left "pageview"] "Anzeigen"]])
      [:p (if valid-until
            (str "Gültig bis " (-> valid-until
                                   datetime/string->date
                                   human/dateformat-long))
            "Unbegrenzt gültig")]
      [certificate-expires-hint]]
     [:div.card-action
      [:button.waves-effect.waves-light.btn
       {:on-click #(rf/dispatch [:set-path [:ui :currently-editing-certificate] current-certificate])} "Bearbeiten"]]]))

(rf/reg-event-db ::finish-upload-certificate
  (fn [db [_ result]]
    (let [uploaded-certificate (-> result
                                   (update :valid-until-date datetime/iso-date->date-string)
                                              ;; Make sure the new certificate appears in the upper, always
                                              ;; visible group of certificates in use
                                   (assoc :in-use? true))
          created? (nil? ((:certificates db) (:id uploaded-certificate)))]
      (-> db
          (assoc-in [:certificates (:id uploaded-certificate)] uploaded-certificate)
          (set-certificate-id (:id uploaded-certificate))
          (assoc-in [:ui :currently-editing-certificate] (when created? uploaded-certificate))
          (assoc-in [:ui :certificate-info-message] (when-not created? "Das Zertifikat wurde schon unter anderem Namen hochgeladen, es wurde nun ausgewählt."))
          (inhibit/unset-save-inhibitor :certificate-upload)))))

(defn- edit-certificate []
  (let [{:keys [url name instructor zpp-course-identifier valid-until-date]} @(rf/subscribe [:get [:ui :currently-editing-certificate]])]
    [:div.card
     [:div.card-content
      [:div.card-title
       "Zertifikat bearbeiten"]
      [widgets/block-input2
       {:id "certificate-name"
        :label-text "Zertifikatsname"
        :cleaner widgets/clean-text
        :value name
        :required? true
        :block-style {:width "100%"}
        :on-save #(rf/dispatch [:set-path [:ui :currently-editing-certificate :name] %])}]
      [widgets/block-input2
       {:id "certificate-instructor"
        :label-text "Trainer Name"
        :cleaner widgets/clean-text
        :value instructor
        :required? true
        :block-style {:width "100%"}
        :on-save #(rf/dispatch [:set-path [:ui :currently-editing-certificate :instructor] %])}]
      [widgets/block-input2
       {:id "certificate-zpp-course-identifier"
        :label-text "Kurs-ID"
        :cleaner widgets/clean-text
        :value zpp-course-identifier
        :required? true
        :block-style {:width "100%"}
        :on-save #(rf/dispatch [:set-path [:ui :currently-editing-certificate :zpp-course-identifier] %])}]
      [:div
       (when url
         [:div.right [:a.btn {:href url :target "_blank"}
                      [:i.material-icons.left "pageview"] "Anzeigen"]])
       [widgets/block-input2
        {:id "certificate-valid-until"
         :type widgets/type-date
         :value valid-until-date
         :label-text "Gültig bis"
         :required? true
         :style {:float "left"}
         :on-save #(rf/dispatch [:set-path [:ui :currently-editing-certificate :valid-until-date] %])}]]]
     [:div.card-action {:style {:clear "both"
                                :margin-top "1em"}}
      [widgets/error-messages @(rf/subscribe [:get [:ui :certificate-save-errors]])]
      [:button.waves-effect.waves-light.btn
       {:on-click #(rf/dispatch [::save-certificate])
        :style {:margin-right "3px"}}
       "Speichern"]
      [:button.waves-effect.waves-light.btn
       {:on-click #(do
                     (rf/dispatch [:set-path [:ui :certificate-save-errors] []])
                     (rf/dispatch [:set-path [:ui :currently-editing-certificate] nil]))}
       "Abbrechen"]]]))

(rf/reg-event-db ::save-certificate [rf/trim-v]
  (fn [current-db]
    (if-not (inhibit/inhibited? :certificate-save (-> current-db :ui :save-inhibitors))
      (inhibit/set-save-inhibitor
       (do
         (POST (get-in current-db [:io :save-certificate-url])
               {:params (clj->js (update (get-in current-db [:ui :currently-editing-certificate])
                                         :valid-until-date datetime/date-string->iso-date))
                :response-format :json
                :headers {"X-CSRFToken" (-> current-db :io :csrf-token)}
                :keywords? true
                :handler #(rf/dispatch [::finish-save-certificate %])
                :error-handler #(rf/dispatch [::save-certificate-error %])})
         current-db)
       :certificate-save)
      current-db)))

(rf/reg-event-db ::finish-save-certificate
  (fn [db [_ result]]
    (let [updated-certificate (-> result
                                  (update :valid-until-date datetime/iso-date->date-string)
                                             ;; Make sure the new certificate appears in the upper, always
                                             ;; visible group of certificates in use
                                  (assoc :in-use? true))]
      (-> db
          (update-in [:ui :currently-editing-certificate] #(if (= (:id updated-certificate) (:id %))
                                                             nil
                                                             %))
          (assoc-in [:certificates (:id updated-certificate)] updated-certificate)
          (inhibit/unset-save-inhibitor :certificate-save)))))

(rf/reg-event-db ::save-certificate-error
  (fn [db [_ result]]
    (let [errors (-> result :response :errors)]
      (-> db
          (assoc-in [:ui :certificate-save-errors] errors)
          (inhibit/unset-save-inhibitor :certificate-save)))))

(rf/reg-sub ::is-editing-certificate?
  :<- [:get [:ui :currently-editing-certificate]]

  (fn [currently-editing-certificate _]
    (-> currently-editing-certificate nil? not)))

(defn- certificate-details []
  (let [current-certificate @(rf/subscribe [::current-certificate])
        is-editing-certificate? @(rf/subscribe [::is-editing-certificate?])]
    (-> [:div.col]
        (into
         (cond
           is-editing-certificate? [{:style {:transition "0.5s ease"}
                                     :class "s7"}
                                    [edit-certificate]]

           current-certificate [{:style {:transition "0.5s ease"}
                                 :class "s6 offset-s1"}
                                [preview-certificate]]

           :else [{:style {:transition "0.5s ease"}
                   :class "s5 offset-s2"}])))))

(defn- certificate-upload []
  (let [is-editing-certificate? (rf/subscribe [::is-editing-certificate?])
        certificate-upload-inhibited? (rf/subscribe [::inhibit/inhibited? :certificate-upload])
        disabled?-atom (reaction (or @is-editing-certificate? @certificate-upload-inhibited?))]
    (fileupload {:id "certificate"
                 :input-element-id "certificate-upload-input"
                 :display-name "certificate-library-upload"
                 :param-name "file"
                 :disabled?-atom disabled?-atom
                 :label "Zertifikat hochladen"
                 :upload-url @(rf/subscribe [:get [:io :upload-certificate-url]])
                 :on-success #(rf/dispatch [::finish-upload-certificate %])})))

(defn- p20-checkbox []
  [:div
   {:style {:margin-bottom "20px"}}
   [:input.filled-in {:type "checkbox"
                      :id "p20-certified"
                      :checked @(rf/subscribe [:get [:product :what :p20-certified?]])
                      :on-change #(rf/dispatch [:toggle [:product :what :p20-certified?]])}]
   [:label.black-text {:for "p20-certified"
                       :style {:height "auto"
                               :line-height "normal"}}
    "Dieses Angebot ist nach § 20 SGB V zertifiziert."]
   [widgets/tooltip {:icon "icon-question-sign"
                     :content "Ihre Kurse entsprechen den Anforderungen nach § 20 SGB V, wenn diese durch die Zentrale Prüfstelle für Prävention zertifiziert sind."}]])

(defn- certificate-library []
  (let [certificate-info-message @(rf/subscribe [:get [:ui :certificate-info-message]])]
    [:div#certificate-library
     [p20-checkbox]
     [:div.col.s12
      [:h5 "Qualifikation"]]
     (when certificate-info-message
       [:div.col.s12>div.alert.alert-info.fade.in
        [:a.close {:on-click #(rf/dispatch [:set-path [:ui :certificate-info-message] nil])} "×"]
        [:div.alertinner.wicon
         [:i.icon-info-sign]
         certificate-info-message]])
     [:div.col
      {:style {:transition "0.5s ease"}
       :class "s5"}
      [all-certificates]
      [certificate-upload]
      (when @(rf/subscribe [::product-form/machtfit-admin?])
        [:a {:href @(rf/subscribe [:get [:io :supplier-list-certificates-url]])} "Zertifikatsverwaltung"])]
     [certificate-details]]))

(defn- contact-salutation []
  [:div.col.s2 {:style {:margin-top "0.5rem"}}
   [widgets/radio-options
    {:name "contact-salutation"
     :choices [[:f "Frau"]
               [:m "Herr"]]
     :current-value @(rf/subscribe [:get [:product :who :contact-salutation]])
     :on-select #(rf/dispatch [:set-path [:product :who :contact-salutation] %])}]])

(defn- contact-first-name []
  [widgets/block-input2
   {:id "contact-first-name"
    :label-text "Vorname"
    :value @(rf/subscribe [:get [:product :who :contact-first-name]])
    :on-save #(rf/dispatch [:set-path [:product :who :contact-first-name] %])}])

(defn- contact-last-name []
  [widgets/block-input2
   {:id "contact-last-name"
    :label-text "Nachname"
    :value @(rf/subscribe [:get [:product :who :contact-last-name]])
    :on-save #(rf/dispatch [:set-path [:product :who :contact-last-name] %])}])

(defn- contact-email []
  [widgets/block-input2
   {:id "contact-email"
    :label-text "E-Mail"
    :value @(rf/subscribe [:get [:product :who :contact-email]])
    :on-save #(rf/dispatch [:set-path [:product :who :contact-email] %])}])

(defn- contact-data []
  [:div
   [:div {:style {:margin-bottom "1rem" :vertical-align "middle"}}
    [contact-salutation]
    [:div.col.s3 [contact-first-name]]
    [:div.col.s4 [contact-last-name]]]
   [:div
    [:div.col.s6 [contact-email]]]])

(defn- supplier-name []
  [:div.col.s12
   [widgets/block-input2 {:id "supplier-name"
                          :label-text "Durchgeführt von"
                          :value @(rf/subscribe [:get [:product :who :supplier-name]])
                          :on-save #(rf/dispatch [:set-path [:product :who :supplier-name] %])}]])

(defn- executing-supplier []
  (let [suppliers (map #(rename-keys % {:name :text}) @(rf/subscribe [:get [:suppliers]]))
        executing-supplier' @(rf/subscribe [:get [:product :who :executing-supplier]])
        executing-supplier-for-select (when executing-supplier'
                                        (rename-keys executing-supplier' {:name :text}))]
    [widgets/block-select {:id "executing-supplier"
                           :label-text [:span "Durchführender Anbieter"
                                        [widgets/tooltip {:icon "icon-question-sign"
                                                          :content "Der durchführende Anbieter kann das Angebot und die Teilnehmerliste im Cockpit sehen und bekommt Buchungsbenachrichtigungen. Er kann das Angebot nicht ändern und keine Teilnehmer hinzufügen oder einen Kurs manuell starten."}]]
                           :choices suppliers
                           :placeholder "--------"
                           :shown-entries 20
                           :value executing-supplier-for-select
                           :on-save #(let [new-executing-supplier (when % (rename-keys % {:text :name}))]
                                       (rf/dispatch [:set-path [:product :who :executing-supplier] new-executing-supplier]))}]))

(defn section-who []
  (let [contact-salutation' @(rf/subscribe [:get [:product :who :contact-salutation]])
        company-product? @(rf/subscribe [:get [:options :company-product?]])
        partner-product? @(rf/subscribe [:get [:options :partner-product?]])
        is-admin? @(rf/subscribe [::product-form/machtfit-admin?])
        is-reselling-product? @(rf/subscribe [:get [:options :is-reselling-product?]])]
    [widgets/section {:anchor "who"
                      :title [review/highlighted-section-title :who "Wer"]
                      :subtitle [:div (if company-product?
                                        (if (= :f contact-salutation')
                                          "ist die Ansprechpartnerin?"
                                          "ist der Ansprechpartner?")
                                        "führt das Angebot durch?")
                                 [review/field-badges :who]]
                      :content (-> [:div
                                    (when (and is-admin? is-reselling-product?)
                                      [:div
                                       [executing-supplier] :certificate-id
                                       [:div.divider.col.s12 {:style {:margin "1rem 0"}}]])
                                    (when partner-product?
                                      [certificate-library])
                                    (when company-product?
                                      [contact-data])
                                    (when company-product?
                                      [supplier-name])
                                    [show-next-section :who]])}]))
